/*
 * Cosmora anonymous traffic counter.
 *
 * Sends one tiny beacon per event to ApeScope's counter endpoint so paid
 * traffic can be counted regardless of the cookie banner. It is built to
 * carry nothing that could identify a visitor:
 *
 *   SENT, and nothing else:  e  event type (visit | add_to_cart | begin_checkout)
 *                            c  utm_campaign     ct  utm_content
 *                            as adset_id (from the ad URL, if present)
 *                            p  landing path     t   timestamp (ms)
 *
 *   NEVER: cookies, localStorage, sessionStorage, IndexedDB, any visitor or
 *   session identifier, fingerprinting signals, referrer, full URL.
 *
 * Carrying the landing campaign to later pages:
 *   1. In the page URL only — at click time, same-origin links get one
 *      `ap_cc` parameter holding the four landing values. Nothing is written
 *      to the device. `ap_cc` is not a utm_* parameter, so Shopify, GA4 and
 *      Meta keep reading the original landing UTMs as before and a later
 *      page never counts as a new visit.
 *   2. Fallback, for anything the URL loses (the language switcher's form
 *      post, a bookmarked /cart): the same four values are written to hidden
 *      cart attributes (_cc_campaign, _cc_content, _cc_adset, _cc_path) on
 *      the cart Shopify already holds for them — after the visitor's own
 *      add-to-cart, and also on a tagged page load when that cart already
 *      holds items (a shopper who filled the cart on an earlier, untagged
 *      visit and returns through an ad straight to checkout never adds to
 *      cart again; #1049). Written only once a cart exists — this script
 *      never creates one — and read back from Liquid on the next page.
 *      The latest tagged visit overwrites an older one (last touch).
 *
 * Every send is a navigator.sendBeacon, which never delays or blocks the
 * page; failures are silent.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://apescope.vercel.app/api/collect';
  var CARRY_PARAM = 'ap_cc';
  var SEP = '|';
  var MAX = 200;

  if (!navigator.sendBeacon || !window.URLSearchParams) return;

  function clip(value, max) {
    return typeof value === 'string' ? value.slice(0, max) : '';
  }

  function contextFromUtm(params) {
    var hasUtm = false;
    params.forEach(function (_v, key) {
      if (key.indexOf('utm_') === 0) hasUtm = true;
    });
    if (!hasUtm) return null;
    var adset = params.get('adset_id') || '';
    return {
      c: clip(params.get('utm_campaign') || '', 120),
      ct: clip(params.get('utm_content') || '', 120),
      as: /^\d{1,32}$/.test(adset) ? adset : '',
      p: clip(window.location.pathname, MAX),
    };
  }

  function contextFromCarry(params) {
    var raw = params.get(CARRY_PARAM);
    if (!raw) return null;
    var parts = raw.split(SEP);
    if (parts.length !== 4) return null;
    return {
      c: clip(parts[0], 120),
      ct: clip(parts[1], 120),
      as: /^\d{1,32}$/.test(parts[2]) ? parts[2] : '',
      p: parts[3].charAt(0) === '/' ? clip(parts[3], MAX) : '',
    };
  }

  function contextFromCart() {
    var a = window.__cosmoraCounterCart;
    if (!a || !a.c) return null;
    return { c: clip(a.c, 120), ct: clip(a.ct || '', 120), as: /^\d{1,32}$/.test(a.as || '') ? a.as : '', p: clip(a.p || '', MAX) };
  }

  // Read once, at load. Dawn's variant picker later rewrites the URL with
  // history.replaceState and drops the query string, so re-reading the URL
  // at add-to-cart time would lose the campaign on the landing page itself.
  var params = new URLSearchParams(window.location.search);
  var fromUtm = contextFromUtm(params);
  var urlContext = fromUtm || contextFromCarry(params);
  var cartContext = contextFromCart();
  var cartHasItems = !!(window.__cosmoraCounterCart && window.__cosmoraCounterCart.n > 0);

  function currentContext() {
    return urlContext || cartContext || { c: '', ct: '', as: '', p: '' };
  }

  function send(eventType, ctx) {
    var payload = { e: eventType, c: ctx.c, ct: ctx.ct, as: ctx.as, p: ctx.p, t: Date.now() };
    try {
      // text/plain keeps this a CORS "simple" request: no preflight, and the
      // response is never read.
      navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify(payload)], { type: 'text/plain' }));
    } catch (e) {
      /* never let counting affect the page */
    }
  }

  // ---- visit -------------------------------------------------------------
  // Only a fresh navigation to a UTM-tagged URL. A reload or back/forward
  // return to the same landing URL is not a new visit; telling them apart
  // needs no storage, the browser reports the navigation type itself.
  function navigationType() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      return nav ? nav.type : 'navigate';
    } catch (e) {
      return 'navigate';
    }
  }
  if (fromUtm && (navigationType() === 'navigate' || navigationType() === 'prerender')) {
    send('visit', fromUtm);
  }

  // ---- carry: decorate same-origin links at click time ----------------------
  function carryValue(ctx) {
    return [ctx.c, ctx.ct, ctx.as, ctx.p].join(SEP);
  }
  if (urlContext) {
    document.addEventListener(
      'click',
      function (event) {
        var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
        if (!link) return;
        var href = link.getAttribute('href');
        if (!href || href.charAt(0) === '#' || /^(mailto|tel|javascript):/i.test(href)) return;
        var url;
        try {
          url = new URL(link.href, window.location.href);
        } catch (e) {
          return;
        }
        if (url.origin !== window.location.origin || url.searchParams.has(CARRY_PARAM)) return;
        url.searchParams.set(CARRY_PARAM, carryValue(urlContext));
        link.href = url.toString();
      },
      true
    );
  }

  // ---- cart-attribute fallback --------------------------------------------
  // keepalive lets the write finish when the page is being left for checkout.
  function syncCartAttributes(keepalive) {
    if (!urlContext || !window.routes || !window.routes.cart_update_url) return;
    if (cartContext && cartContext.c === urlContext.c && cartContext.ct === urlContext.ct && cartContext.as === urlContext.as && cartContext.p === urlContext.p) return;
    cartContext = urlContext;
    try {
      fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: { _cc_campaign: urlContext.c, _cc_content: urlContext.ct, _cc_adset: urlContext.as, _cc_path: urlContext.p },
        }),
        keepalive: keepalive === true,
      }).catch(function () {});
    } catch (e) {
      /* ignore */
    }
  }

  // A cart that already holds items takes this visit's campaign now, so a
  // shopper who goes straight to checkout without adding again keeps it.
  if (cartHasItems) syncCartAttributes(false);

  // ---- add_to_cart ----------------------------------------------------------
  // Product form (incl. sticky bar and quick-add): Dawn publishes
  // `cart-update` with source 'product-form' only after /cart/add succeeded.
  // Cart drawer and cart page: a quantity increase, confirmed by the
  // 'cart-items' cart-update that follows a successful /cart/change.
  var pendingIncrease = {};
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || input.name !== 'updates[]' || !input.closest || !input.closest('cart-items, cart-drawer-items')) return;
      var before = parseInt(input.getAttribute('value'), 10);
      var after = parseInt(input.value, 10);
      pendingIncrease[input.id || input.dataset.index || 'line'] = !isNaN(before) && !isNaN(after) && after > before;
    },
    true
  );

  function onCartUpdate(event) {
    if (!event) return;
    if (event.source === 'product-form') {
      send('add_to_cart', currentContext());
      syncCartAttributes();
    } else if (event.source === 'cart-items') {
      var increased = false;
      for (var key in pendingIncrease) if (pendingIncrease[key]) increased = true;
      pendingIncrease = {};
      if (increased && event.cartData && !event.cartData.errors) {
        send('add_to_cart', currentContext());
        syncCartAttributes();
      }
    }
  }

  function subscribeWhenReady() {
    // pubsub.js declares `subscribe` as a plain global function; constants.js
    // declares PUB_SUB_EVENTS with `const`, which is global but not a window
    // property, hence the typeof guard.
    if (typeof window.subscribe !== 'function') return;
    var eventName = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.cartUpdate : 'cart-update';
    window.subscribe(eventName, onCartUpdate);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', subscribeWhenReady);
  else subscribeWhenReady();

  // ---- begin_checkout -------------------------------------------------------
  // The checkout buttons are native submits (name="checkout") of the drawer
  // and cart-page forms. Bubble phase + defaultPrevented check, so a submit
  // the TikTok in-app-browser gate cancels is not counted.
  var checkoutSentAt = 0;
  function sendCheckoutOnce() {
    var now = Date.now();
    if (now - checkoutSentAt < 2000) return;
    checkoutSentAt = now;
    send('begin_checkout', currentContext());
    syncCartAttributes(true);
  }
  document.addEventListener('submit', function (event) {
    if (event.defaultPrevented) return;
    var submitter = event.submitter;
    if (submitter && submitter.name === 'checkout') sendCheckoutOnce();
  });
  // Express wallet buttons go straight to checkout without a form submit;
  // count a press of one as a checkout start too. On /cart they check out
  // the cart itself, attributes included. The product-page buttons (off on
  // the live theme today) check out that one product and skip the cart, so
  // its attributes do not reach them: those are counted, but carry no
  // campaign.
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented) return;
    var target = event.target;
    if (target && target.closest && target.closest('.additional-checkout-buttons, shopify-accelerated-checkout-cart, shopify-accelerated-checkout, .shopify-payment-button')) sendCheckoutOnce();
  });
})();
